import { Global, css } from '@emotion/react';
import { Slider } from 'antd';
import { useCallback, useEffect, useMemo, useRef, type CSSProperties } from 'react';
import { colorConstants } from 'config/colorConstants';

const DEFAULT_THEME = {
    sliderClassName: 'vertical-range-slider',
    wrapperClassName: 'flex h-full w-[26px] flex-col items-center justify-center gap-[2px]',
    labelClassName: 'w-[14px] shrink-0 text-center text-[10px] leading-[14px]',
    sliderContainerClassName: 'relative flex-1 overflow-visible',
    sliderContainerWidth: 23,
    sliderContainerStyle: { marginTop: 0, marginBottom: 12 },
    sliderMargin: '6px 0px',
    railWidth: 13,
    trackWidth: 14,
    railColor: colorConstants.sliderRailLight,
    trackColor: colorConstants.grey,
    handleWidth: 23,
    handleHeight: 9,
    handleBorderWidth: 1.5,
    handleBorderColor: colorConstants.breadcrumbSlash,
    handleBackgroundColor: colorConstants.white,
    handleInnerLineColor: colorConstants.sliderHandleLine,
    handleInnerLineWidth: 10,
    handleInnerLineHeight: 0.5,
    handleInnerLineTopOffset: 1.5,
    showHandleInnerLines: true,
    tooltipBackgroundColor: colorConstants.sliderTooltipBg,
    tooltipTextColor: colorConstants.white,
    tooltipPadding: '4px 8px',
    tooltipFontSize: 12,
    tooltipFontWeight: 400,
    tooltipLineHeight: 22,
    tooltipBorderRadius: 4,
    tooltipOffsetBottom: 8,
    hoverHandleBorderColor: colorConstants.breadcrumbSlash,
    hoverHandleShadow: `0 0 0 2px ${colorConstants.sliderHandleOutlineShadow}`,
    disabledOpacity: 0.45,
};

type VerticalRangeSliderProps = {
    min: number;
    max: number;
    value: [number, number];
    onChange: (nextRange: [number, number]) => void;
    formatLabel: (value: number) => string;
    step: number;
    disabled?: boolean;
    theme?: Partial<typeof DEFAULT_THEME>;
};

const buildSliderStyles = (theme: typeof DEFAULT_THEME) => {
    const innerLineOffset = theme.handleInnerLineTopOffset;

    return css`
        .${theme.sliderClassName} .ant-tooltip.ant-slider-tooltip .ant-tooltip-inner {
            min-width: auto;
            min-height: auto;
            padding: ${theme.tooltipPadding};
            background-color: ${theme.tooltipBackgroundColor};
            color: ${theme.tooltipTextColor};
            font-size: ${theme.tooltipFontSize}px;
            font-weight: ${theme.tooltipFontWeight};
            line-height: ${theme.tooltipLineHeight}px;
            border-radius: ${theme.tooltipBorderRadius}px;
            box-shadow: none;
        }

        .${theme.sliderClassName} .ant-tooltip.ant-slider-tooltip.ant-tooltip-placement-top {
            padding-bottom: ${theme.tooltipOffsetBottom}px;
        }

        .${theme.sliderClassName} .ant-tooltip.ant-slider-tooltip .ant-tooltip-arrow {
            display: none;
        }

        .${theme.sliderClassName} .ant-tooltip.ant-slider-tooltip .ant-tooltip-arrow-content {
            display: none;
        }

        .${theme.sliderClassName}.${theme.sliderClassName}--disabled {
            opacity: ${theme.disabledOpacity};
            pointer-events: none;
        }

        .${theme.sliderClassName} .ant-slider-handle {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            transition: box-shadow 0.2s ease, border-color 0.2s ease;
            box-sizing: border-box;
            overflow: hidden;
        }

        .${theme.sliderClassName} .ant-slider-handle::before,
        .${theme.sliderClassName} .ant-slider-handle::after {
            content: '';
            position: absolute;
            inset: auto;
            left: 50%;
            width: ${theme.showHandleInnerLines ? `${theme.handleInnerLineWidth}px` : '0'};
            height: ${theme.showHandleInnerLines ? `${theme.handleInnerLineHeight}px` : '0'};
            border-radius: 9999px;
            background-color: ${theme.handleInnerLineColor};
            transform: translate(-50%, -50%);
            display: ${theme.showHandleInnerLines ? 'block' : 'none'};
        }

        .${theme.sliderClassName} .ant-slider-handle::before {
            top: calc(50% - ${innerLineOffset}px);
        }

        .${theme.sliderClassName} .ant-slider-handle::after {
            top: calc(50% + ${innerLineOffset}px);
        }

        .${theme.sliderClassName} .ant-slider-handle:hover,
        .${theme.sliderClassName} .ant-slider-handle:focus,
        .${theme.sliderClassName} .ant-slider-handle.ant-tooltip-open {
            border-color: ${theme.hoverHandleBorderColor} !important;
            box-shadow: ${theme.hoverHandleShadow} !important;
        }
    `;
};

const VerticalRangeSlider = ({
    min,
    max,
    value,
    onChange,
    formatLabel,
    step,
    disabled = false,
    theme,
}: VerticalRangeSliderProps) => {
    const previousValueRef = useRef(value);
    const sliderContainerRef = useRef<HTMLDivElement>(null);
    const resolvedTheme = useMemo(
        () => ({
            ...DEFAULT_THEME,
            ...theme,
        }),
        [theme],
    );
    const handleSideOffset = useMemo(
        () => (resolvedTheme.handleWidth - resolvedTheme.railWidth) / 2,
        [resolvedTheme.handleWidth, resolvedTheme.railWidth],
    );
    const labelAlignmentStyle = useMemo<CSSProperties>(
        () => ({
            width: resolvedTheme.sliderContainerWidth,
            textAlign: 'center',
            fontVariantNumeric: 'tabular-nums',
            color: colorConstants.sliderLabelText,
        }),
        [resolvedTheme.sliderContainerWidth],
    );

    useEffect(() => {
        previousValueRef.current = value;
    }, [value]);

    const handleRangeChange = (nextRange: number | [number, number]) => {
        if (disabled) return;
        if (!Array.isArray(nextRange) || nextRange.length !== 2) return;

        let low = Math.min(nextRange[0], nextRange[1]);
        let high = Math.max(nextRange[0], nextRange[1]);
        const [prevLow, prevHigh] = previousValueRef.current;
        const lowMoved = Math.abs(low - prevLow) >= step / 2;
        const highMoved = Math.abs(high - prevHigh) >= step / 2;

        if (high - low < step) {
            if (lowMoved && !highMoved) {
                low = Math.max(min, high - step);
            } else if (highMoved && !lowMoved) {
                high = Math.min(max, low + step);
            } else {
                const midpoint = (low + high) / 2;
                low = Math.max(min, midpoint - step / 2);
                high = Math.min(max, midpoint + step / 2);
                if (high - low < step) {
                    if (high < max) {
                        high = Math.min(max, low + step);
                    } else {
                        low = Math.max(min, high - step);
                    }
                }
            }
        }

        if (low >= high) return;

        const nextValue: [number, number] = [low, high];
        previousValueRef.current = nextValue;
        onChange(nextValue);
    };

    const formatTooltipValue = useCallback(
        (val?: number) => formatLabel(typeof val === 'number' ? val : min),
        [formatLabel, min],
    );

    return (
        <>
            <Global styles={buildSliderStyles(resolvedTheme)} />
            <div className={resolvedTheme.wrapperClassName}>
                <span className={resolvedTheme.labelClassName} style={labelAlignmentStyle}>
                    {formatLabel(max)}
                </span>
                <div
                    ref={sliderContainerRef}
                    className={`${resolvedTheme.sliderContainerClassName} ${resolvedTheme.sliderClassName}${
                        disabled ? ` ${resolvedTheme.sliderClassName}--disabled` : ''
                    }`}
                    style={{
                        width: resolvedTheme.sliderContainerWidth,
                        ...resolvedTheme.sliderContainerStyle,
                    }}
                >
                    <Slider
                        vertical
                        range
                        disabled={disabled}
                        min={min}
                        max={max}
                        step={step}
                        value={value}
                        // @ts-expect-error rc-slider range props are omitted from antd Slider types
                        allowCross={false}
                        pushable={step}
                        onChange={handleRangeChange}
                        className="absolute z-10 m-0 h-full"
                        style={{
                            left: handleSideOffset,
                            margin: resolvedTheme.sliderMargin,
                            width: resolvedTheme.railWidth,
                            padding: 0,
                        }}
                        railStyle={{
                            backgroundColor: resolvedTheme.railColor,
                            width: resolvedTheme.railWidth,
                            borderRadius: 9999,
                            padding: 0,
                        }}
                        trackStyle={[
                            {
                                backgroundColor: resolvedTheme.trackColor,
                                width: resolvedTheme.trackWidth,
                                borderRadius: 9999,
                            },
                        ]}
                        handleStyle={[
                            {
                                width: resolvedTheme.handleWidth,
                                height: resolvedTheme.handleHeight,
                                marginLeft: -handleSideOffset,
                                marginTop: 0,
                                borderRadius: 9999,
                                border: `${resolvedTheme.handleBorderWidth}px solid ${resolvedTheme.handleBorderColor}`,
                                backgroundColor: resolvedTheme.handleBackgroundColor,
                                boxShadow: 'none',
                                cursor: 'ns-resize',
                            },
                            {
                                width: resolvedTheme.handleWidth,
                                height: resolvedTheme.handleHeight,
                                marginLeft: -handleSideOffset,
                                marginTop: 0,
                                borderRadius: 9999,
                                border: `${resolvedTheme.handleBorderWidth}px solid ${resolvedTheme.handleBorderColor}`,
                                backgroundColor: resolvedTheme.handleBackgroundColor,
                                boxShadow: 'none',
                                cursor: 'ns-resize',
                            },
                        ]}
                        tooltip={{
                            formatter: formatTooltipValue,
                            placement: 'top',
                            getPopupContainer: () => sliderContainerRef.current ?? document.body,
                        }}
                    />
                </div>
                <span className={resolvedTheme.labelClassName} style={labelAlignmentStyle}>
                    {formatLabel(min)}
                </span>
            </div>
        </>
    );
};

export default VerticalRangeSlider;
